import PropTypes from "prop-types";
import React, { useState, useMemo } from "react";
import styled from "styled-components";
import { rarifyIdRegex } from "../../../modules/constants";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import Checkbox from "../../ui-library/Checkbox/Checkbox";
import LabelledInput from "../../ui-library/LabelledInput/LabelledInput";
import { useTranslation } from "react-i18next";
import AlertUGNX from "../../ToastsAndAlerts/AlertUGNX";
import { AlertType } from "../../ToastsAndAlerts/constants";

const StyledEsignature = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-column-gap: 10px;
  grid-row-gap: 10px;

  @media (max-width: ${(props) =>
      props?.withGuardian ? "1715px" : "1160px"}) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const StyledCheckboxLabel = styled.div`
  font-weight: 600;
  margin-left: 15px;
  cursor: pointer;
`;

const StyledCheckboxWrapper = styled.div`
  display: flex;
  margin-top: 30px;
  margin-bottom: 15px;
`;

const ESignature = ({
  withGuardian,
  surveyDefinitionId,
  responseModel,
  onComplete,
  errorMessage,
}) => {
  const initialState = {
    password: null,
    rarifyId: null,
    eSignatureList: [
      {
        name: null,
        lastName: null,
        meaning: null,
        surveyDefinitionID: surveyDefinitionId,
        isAssent: false,
        responseModel: responseModel,
      },
    ],
    eligibilityCriteria: true,
  };

  const { t } = useTranslation();

  const [data, setData] = useState(initialState);
  const [check, setCheck] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (key, val) => {
    setData({
      ...data,
      [key]: val,
    });
  };
  const handleNestedChange = (index, key, val) => {
    let arrCopy = data.eSignatureList;
    arrCopy[index] = {
      ...arrCopy[index],
      [key]: val,
    };
    setData({
      ...data,
      eSignatureList: arrCopy,
    });
  };

  const isRarifyIdValid = useMemo(() => {
    if (!rarifyIdRegex.test(data?.rarifyId)) {
      return false;
    }
    return true;
  }, [data]);

  const validateRarifyId = () => {
    if (!data?.rarifyId) {
      setErrors({
        ...errors,
        rarifyId: isRarifyIdValid
          ? undefined
          : t("errorText.esignature.required.field"),
      });
    } else {
      setErrors({
        ...errors,
        rarifyId: isRarifyIdValid
          ? undefined
          : t("errorText.esignature.invalid.field"),
      });
    }
  };

  return (
    <div>
      <StyledEsignature withGuardian>
        <LabelledInput
          tabIndex={1}
          className="mt-4"
          title="Participant's Legal First Name"
          data-testid="participant-first-name-input"
          type="text"
          value={data.eSignatureList[0].name}
          onChange={(e) => handleNestedChange(0, "name", e.target.value)}
        />
        <LabelledInput
          tabIndex={2}
          className="mt-4"
          title="Participant's Legal Last Name"
          data-testid="participant-last-name-input"
          type="text"
          value={data.eSignatureList[0].lastName}
          onChange={(e) => handleNestedChange(0, "lastName", e.target.value)}
        />
        <LabelledInput
          tabIndex={3}
          className="mt-4"
          title="Rarify ID"
          type="text"
          value={data.rarifyId}
          placeholder="XXX-XX-XXX"
          data-testid="rarify-id-input"
          hasError={!!errors?.rarifyId}
          errorMessage={errors?.rarifyId}
          onChange={(e) => handleChange("rarifyId", e.target.value)}
          onBlur={() => validateRarifyId()}
        />
        <LabelledInput
          tabIndex={4}
          className="mt-4"
          title="Password"
          type="password"
          data-testid="password-input"
          value={data.password}
          onCopy={(e) => e.preventDefault()}
          onDrag={(e) => e.preventDefault()}
          onDrop={(e) => e.preventDefault()}
          onPaste={(e) => e.preventDefault()}
          onChange={(e) => handleChange("password", e.target.value)}
        />
        {withGuardian && (
          <LabelledInput
            tabIndex={5}
            className="mt-4 w-100"
            title="Parent/Guardian's Legal Last Name"
            data-testid="guardian-last-name-input"
            type="text"
          />
        )}
        {withGuardian && (
          <LabelledInput
            tabIndex={6}
            className="mt-4 w-100"
            title="Parent/Guardian's Legal First Name"
            type="text"
            data-testid="guardian-first-name-input"
          />
        )}
      </StyledEsignature>
      <div>
        <StyledCheckboxWrapper tabIndex="0">
          <Checkbox
            data-testid="confirm-checkbox"
            icon={true}
            square={true}
            checked={check}
            onClick={() => setCheck(!check)}
          />
          <StyledCheckboxLabel data-testid="confirm-checkbox-label">
            I understand after I sign the consent, I can access the signed
            consent on my participant page.
          </StyledCheckboxLabel>
        </StyledCheckboxWrapper>
      </div>
      {errorMessage && (
        <AlertUGNX type={AlertType.DANGER}>
          <div dangerouslySetInnerHTML={{ __html: errorMessage }}></div>
        </AlertUGNX>
      )}
      <div className="d-flex justify-content-end mt-4">
        <PrimaryButton
          data-testid="confirm-button"
          disabled={
            !data.eSignatureList[0].name ||
            !data.eSignatureList[0].lastName ||
            !data.password ||
            !isRarifyIdValid ||
            !check
          }
          onClick={() => onComplete(data)}
        >
          Confirm Signature
        </PrimaryButton>
      </div>
    </div>
  );
};

ESignature.propTypes = {
  onComplete: PropTypes.func,
  withGuardian: PropTypes.bool,
};

export default ESignature;
